import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import { supabase_admin } from '../supabase_admin';
import Display from './Display';
import '../styles/FileList.css';
import { 
  Trash2, Banknote, FileDigit, FileAxis3d, ArrowLeftToLine, 
  GraduationCap, BrainCircuit, DraftingCompass, LandPlot, 
  ReceiptEuro, PencilRuler, ChartGantt, ScrollText, Ellipsis, 
  Lightbulb, Search, Eye, FileLock, LockOpen, SpellCheck2, FolderInput, Trash,
  SquareArrowOutUpRight, Check, Expand
} from 'lucide-react';

function FileList() {
  const { authUserId, selectedWorkspaceId } = useWorkspace();
  const { language } = useLanguage();
  const [files, setFiles] = useState([]);
  const [sortColumn, setSortColumn] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [hasSensitiveAccess, setHasSensitiveAccess] = useState(false);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayUrl, setDisplayUrl] = useState(null);
  const [displayTextContent, setDisplayTextContent] = useState(null);
  const [displayType, setDisplayType] = useState(null);
  const [displayFileTitle, setDisplayFileTitle] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [currentAssignFileId, setCurrentAssignFileId] = useState(null);
  const [assignSelectedCategory, setAssignSelectedCategory] = useState(null);
  const [assignSelectedSubCategory, setAssignSelectedSubCategory] = useState(null);

  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameFileId, setRenameFileId] = useState(null);
  const [newFileName, setNewFileName] = useState('');

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState(null);

  const bucketName = "7e8835a5_5c90_487a_82b0_62758d80435c";

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        // Get user's system role
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('system_role')
          .eq('id', authUserId)
          .single();

        if (profileError) throw profileError;
        setUserRole(profileData?.system_role);

        // Get user's workspace role
        const { data: workspaceRoleData, error: workspaceRoleError } = await supabase
          .from('user_workspace_roles')
          .select('has_sensitive_access')
          .eq('user_id', authUserId)
          .eq('workspace_id', selectedWorkspaceId)
          .single();

        if (workspaceRoleError) throw workspaceRoleError;
        setHasSensitiveAccess(workspaceRoleData?.has_sensitive_access || false);
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    };

    if (authUserId && selectedWorkspaceId) {
      fetchUserRole();
    }
  }, [authUserId, selectedWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setError('Workspace ID is missing.');
      return;
    }

    const fetchFiles = async () => {
      setLoading(true);
      setError('');

      try {
        let query = supabase
          .from('documents')
          .select('id, name, updated_at, updated_by, folder_path, type, content, workspace_id, is_sensitive')
          .eq('workspace_id', selectedWorkspaceId)
          .eq('is_deleted', false)
          .eq('is_archived', false)
          .order(sortColumn, { ascending: sortOrder === 'asc' });

        if (selectedCategory === 'general') {
          query = query.ilike('folder_path', 'input/general%');
        } else if (selectedCategory === 'financials') {
          if (!selectedSubCategory) {
            query = query.ilike('folder_path', 'input/financials%');
          } else {
            query = query.ilike('folder_path', `input/financials/${selectedSubCategory}%`);
          }
        } else if (selectedCategory === 'technical') {
          if (!selectedSubCategory) {
            query = query.ilike('folder_path', 'input/technical%');
          } else {
            query = query.ilike('folder_path', `input/technical/${selectedSubCategory}%`);
          }
        } else if (selectedCategory === 'timing') {
          query = query.ilike('folder_path', 'input/timing%');
        } else if (selectedCategory === 'applications') {
          query = query.ilike('folder_path', 'input/applications%');
        } else if (selectedCategory === 'other') {
          if (!selectedSubCategory) {
            query = query.ilike('folder_path', 'input/other%');
          } else {
            query = query.ilike('folder_path', `input/other/${selectedSubCategory}%`);
          }
        }

        const { data: documents, error: documentError } = await query;
        if (documentError) throw documentError;

        const updatedByIds = documents.map((doc) => doc.updated_by);
        const { data: profiles, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, fullname')
          .in('id', updatedByIds);

        if (profileError) throw profileError;

        const documentsWithFullName = documents.map((doc) => {
          const profile = profiles.find((p) => p.id === doc.updated_by);
          return { ...doc, fullname: profile ? profile.fullname : 'Unknown' };
        });

        // Filter sensitive files based on user role and access
        const filteredDocuments = documentsWithFullName.filter(doc => {
          if (!doc.is_sensitive) return true;
          return userRole?.startsWith('org') || hasSensitiveAccess;
        });

        setFiles(filteredDocuments);
      } catch (err) {
        setError('Failed to fetch files.');
        console.error(err);
      }

      setLoading(false);
    };

    fetchFiles();
  }, [
    selectedWorkspaceId,
    sortColumn,
    sortOrder,
    selectedCategory,
    selectedSubCategory,
    userRole,
    hasSensitiveAccess
  ]);

  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder((prevOrder) =>
      sortColumn === column && prevOrder === 'asc' ? 'desc' : 'asc'
    );
  };

  const handleMarkSensitive = async (documentId) => {
    try {
      const file = files.find(f => f.id === documentId);
      const newSensitiveValue = !file.is_sensitive;
      
      const { error } = await supabase
        .from('documents')
        .update({ is_sensitive: newSensitiveValue })
        .eq('id', documentId);
      if (error) throw error;
      
      setSuccessMessage(`Document ${newSensitiveValue ? 'marked as sensitive' : 'unmarked as sensitive'} successfully.`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      setFiles(prevFiles =>
        prevFiles.map(file =>
          file.id === documentId
            ? { ...file, is_sensitive: newSensitiveValue }
            : file
        )
      );
    } catch (err) {
      console.error('Failed to update sensitivity:', err);
      setError('Failed to update sensitivity.');
    }
  };

  const handleRename = async (documentId) => {
    const fileToRename = files.find((file) => file.id === documentId);
    if (!fileToRename) return;
    
    setRenameFileId(documentId);
    setRenameModalVisible(true);
    setNewFileName(fileToRename.name);
  };

  const handleRenameConfirm = async () => {
    if (!newFileName.trim()) return;
    
    const fileToRename = files.find((file) => file.id === renameFileId);
    if (!fileToRename) return;
    const oldName = fileToRename.name;
    const workspaceFolder = fileToRename.workspace_id;
  
    try {
      const { error } = await supabase
        .from('documents')
        .update({ name: newFileName })
        .eq('id', renameFileId);
      if (error) throw error;
  
      setSuccessMessage('File renamed successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      console.log(`Document ${renameFileId} updated: name=${newFileName}`);
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === renameFileId ? { ...file, name: newFileName } : file
        )
      );
  
      const oldPath = `${workspaceFolder}/${oldName}`;
      const newPath = `${workspaceFolder}/${newFileName}`;
  
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(oldPath, 3600); 
      if (signedUrlError) throw signedUrlError;
      if (!signedUrlData?.signedUrl) {
        throw new Error("Unable to generate signed URL for the file.");
      }
  
      const response = await fetch(signedUrlData.signedUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file data.");
      }
      const blob = await response.blob();

      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(newPath, blob);
      if (uploadError) throw uploadError;
  
      const { error: removeError } = await supabase.storage
        .from(bucketName)
        .remove([oldPath]);
      if (removeError) throw removeError;
  
    } catch (err) {
      console.error('Failed to rename file:', err);
      setError('You already have a file with this name.');
    }
    setRenameModalVisible(false);
  };

  const handleDelete = async (documentId) => {
    setDeleteFileId(documentId);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    const fileToDelete = files.find((file) => file.id === deleteFileId);
    if (!fileToDelete) {
      console.error('File not found in local state');
      return;
    }
  
    try {
      const workspaceFolder = fileToDelete.workspace_id;
      const oldFilePath = `${workspaceFolder}/${fileToDelete.name}`;
      const newFileName = `${fileToDelete.name}_${deleteFileId}`;
      const news3_key = `${workspaceFolder}/trash`;
      const newFilePath = `${workspaceFolder}/trash/${newFileName}`;
  
      const { error: copyError } = await supabase_admin.storage
        .from(bucketName)
        .copy(oldFilePath, newFilePath);
      if (copyError) throw copyError;
  
      const { error: removeError } = await supabase_admin.storage
        .from(bucketName)
        .remove([oldFilePath]);
      if (removeError) {
        console.error('Error removing the original file:', removeError);
      }
  
      const { data: signedUrlData, error: signedUrlError } = await supabase_admin.storage
        .from(bucketName)
        .createSignedUrl(newFilePath, 60 * 60);
      if (signedUrlError) {
        console.error('Error creating signed URL:', signedUrlError);
      }
  
      const { error: updateError } = await supabase_admin
        .from('documents')
        .update({
          name: String(newFileName), 
          updated_at: new Date().toISOString(), 
          is_deleted: true, 
          updated_by: authUserId,
          s3_key: news3_key,
        })
        .eq('id', deleteFileId);
      if (updateError) throw updateError;
  
      setSuccessMessage('Document deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Trigger a refetch of files
      const fetchFiles = async () => {
        try {
          let query = supabase
            .from('documents')
            .select('id, name, updated_at, updated_by, folder_path, type, content, workspace_id, is_sensitive')
            .eq('workspace_id', selectedWorkspaceId)
            .eq('is_deleted', false)
            .eq('is_archived', false)
            .order(sortColumn, { ascending: sortOrder === 'asc' });

          if (selectedCategory === 'general') {
            query = query.ilike('folder_path', 'input/general%');
          } else if (selectedCategory === 'financials') {
            if (!selectedSubCategory) {
              query = query.ilike('folder_path', 'input/financials%');
            } else {
              query = query.ilike('folder_path', `input/financials/${selectedSubCategory}%`);
            }
          } else if (selectedCategory === 'technical') {
            if (!selectedSubCategory) {
              query = query.ilike('folder_path', 'input/technical%');
            } else {
              query = query.ilike('folder_path', `input/technical/${selectedSubCategory}%`);
            }
          } else if (selectedCategory === 'timing') {
            query = query.ilike('folder_path', 'input/timing%');
          } else if (selectedCategory === 'applications') {
            query = query.ilike('folder_path', 'input/applications%');
          } else if (selectedCategory === 'other') {
            if (!selectedSubCategory) {
              query = query.ilike('folder_path', 'input/other%');
            } else {
              query = query.ilike('folder_path', `input/other/${selectedSubCategory}%`);
            }
          }

          const { data: documents, error: documentError } = await query;
          if (documentError) throw documentError;

          const updatedByIds = documents.map((doc) => doc.updated_by);
          const { data: profiles, error: profileError } = await supabase
            .from('user_profiles')
            .select('id, fullname')
            .in('id', updatedByIds);

          if (profileError) throw profileError;

          const documentsWithFullName = documents.map((doc) => {
            const profile = profiles.find((p) => p.id === doc.updated_by);
            return { ...doc, fullname: profile ? profile.fullname : 'Unknown' };
          });

          // Filter sensitive files based on user role and access
          const filteredDocuments = documentsWithFullName.filter(doc => {
            if (!doc.is_sensitive) return true;
            return userRole?.startsWith('org') || hasSensitiveAccess;
          });

          setFiles(filteredDocuments);
        } catch (err) {
          setError('Failed to fetch files.');
          console.error(err);
        }
      };

      fetchFiles();
    } catch (err) {
      console.error('Failed to rename file:', err);
      setError('Failed to rename file.');
    }
    setDeleteModalVisible(false);
  };
  

  const handleViewFile = async (file) => {
    if (file.type === "url/reference") {
      window.open(file.content, "_blank");
    } else if (
      file.type.startsWith("application/") ||
      file.type.startsWith("image/")
    ) {
      const bucket = String(bucketName);
      const workspaceFolder = String(file.workspace_id);
      const filePath = `${workspaceFolder}/${file.name}`;
      
      const { data: signedUrlData, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600);
      
      if (error) {
        console.error("Error generating signed URL:", error);
        return;
      }
      
      if (signedUrlData?.signedUrl) {
        window.open(signedUrlData.signedUrl, "_blank");
      } else {
        console.error("Unable to fetch signed URL");
      }
    } else {
      console.error("Unsupported document type");
    }
  };

  const handleDisplayFile = async (file) => {
    setDisplayType(file.type);
    setDisplayFileTitle(file.name);
    
    if (file.type === "url/reference") {
      setDisplayUrl(file.content);
      setDisplayTextContent(null);
      setDisplayModalOpen(true);
    } else if (file.type?.startsWith("text/")) {
      setDisplayUrl(null);
      setDisplayTextContent(file.content);
      setDisplayModalOpen(true);
    } else if (
      file.type.startsWith("application/") ||
      file.type.startsWith("image/")
    ) {
      const bucket = String(bucketName);
      const workspaceFolder = String(file.workspace_id);
      const filePath = `${workspaceFolder}/${file.name}`;
      
      const { data: signedUrlData, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600);
      
      if (error) {
        console.error("Error generating signed URL:", error);
        return;
      }
      
      if (signedUrlData?.signedUrl) {
        setDisplayUrl(signedUrlData.signedUrl);
        setDisplayTextContent(null);
        setDisplayModalOpen(true);
      } else {
        console.error("Unable to fetch signed URL");
      }
    } else {
      console.error("Unsupported document type");
    }
  };
   

  const handleAssignFolderClick = (fileId) => {
    setCurrentAssignFileId(fileId);
    setAssignModalVisible(true);
    setAssignSelectedCategory(null);
    setAssignSelectedSubCategory(null);
  };

  const updateFolderPath = async (fileId, folderPath) => {
    try {
      const { error } = await supabase
        .from('documents')
        .update({ folder_path: folderPath })
        .eq('id', fileId);
      if (error) throw error;
      setSuccessMessage('Assigned to folder successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === fileId ? { ...file, folder_path: folderPath } : file
        )
      );
    } catch (err) {
      setError('Failed to update folder path.');
      console.error(err);
    }
    setAssignModalVisible(false);
  };

  const handleAssignCategoryClick = (category) => {
    if (
      category === 'general' ||
      category === 'timing' ||
      category === 'applications'
    ) {
      const folderPath = `input/${category}`;
      updateFolderPath(currentAssignFileId, folderPath);
    } else {
      setAssignSelectedCategory(category);
    }
  };

  const handleAssignSubCategoryClick = (subCategory) => {
    const folderPath = `input/${assignSelectedCategory}/${subCategory}`;
    updateFolderPath(currentAssignFileId, folderPath);
  };

  const handleAssignGoBack = () => {
    if (assignSelectedSubCategory) {
      setAssignSelectedSubCategory(null);
    } else {
      setAssignSelectedCategory(null);
    }
  };

  const handleGeneralCompanyClick = () => {
    setSelectedCategory('general');
    setSelectedSubCategory(null);
  };

  const handleFinancialsClick = () => {
    setSelectedCategory('financials');
    setSelectedSubCategory(null);
  };

  const handleTechnicalClick = () => {
    setSelectedCategory('technical');
    setSelectedSubCategory(null);
  };

  const handlePayrollClick = () => setSelectedSubCategory('payroll');
  const handleInvoicesClick = () => setSelectedSubCategory('invoices');
  const handleReportsClick = () => setSelectedSubCategory('reports');

  const handleResearchClick = () => setSelectedSubCategory('research');
  const handleDocumentationClick = () => setSelectedSubCategory('documentation');
  const handleDrawingsClick = () => setSelectedSubCategory('drawings');

  const handleTimingClick = () => {
    setSelectedCategory('timing');
    setSelectedSubCategory(null);
  };
  const handleOtherAppsClick = () => {
    setSelectedCategory('applications');
    setSelectedSubCategory(null);
  };

  const handleOtherClick = () => {
    setSelectedCategory('other');
    setSelectedSubCategory(null);
  };
  const handleCompetitionInspirationClick = () => {
    setSelectedSubCategory('competition');
  };

  const handleGoBack = () => {
    if (selectedSubCategory) {
      setSelectedSubCategory(null);
    } else {
      setSelectedCategory(null);
    }
  };

  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="file-list-container">

      <hr className="divider" />

      <div className="breadcrumb">
        <span>{language === 'en' ? 'Data Room' : 'Datenraum'}</span>
        {selectedCategory ? (
          <>
            {' > '}
            <span className="active-breadcrumb">
              {selectedSubCategory ? (
                <>
                  {selectedCategory === 'general'
                    ? language === 'en' ? 'General/Company' : 'Allgemein'
                    : selectedCategory === 'financials'
                    ? language === 'en' ? 'Financials' : 'Finanzen'
                    : selectedCategory === 'technical'
                    ? language === 'en' ? 'Technical/Scientific' : 'Technisch'
                    : selectedCategory === 'timing'
                    ? language === 'en' ? 'Timing' : 'Zeitplan'
                    : selectedCategory === 'applications'
                    ? language === 'en' ? 'Other Applications' : 'Apps'
                    : selectedCategory === 'other'
                    ? language === 'en' ? 'Other' : 'Sonstiges'
                    : selectedCategory}
                  {' > '}
                  {selectedSubCategory === 'payroll'
                    ? language === 'en' ? 'Payroll' : 'Gehalt'
                    : selectedSubCategory === 'invoices'
                    ? language === 'en' ? 'Invoices' : 'Rechnungen'
                    : selectedSubCategory === 'reports'
                    ? language === 'en' ? 'Reports' : 'Berichte'
                    : selectedSubCategory === 'research'
                    ? language === 'en' ? 'Research Papers' : 'Forschung'
                    : selectedSubCategory === 'documentation'
                    ? language === 'en' ? 'Documentation' : 'Dokumentation'
                    : selectedSubCategory === 'drawings'
                    ? language === 'en' ? 'Drawings' : 'Zeichnungen'
                    : selectedSubCategory === 'competition'
                    ? language === 'en' ? 'Competition & Inspiration' : 'Wettbewerb'
                    : selectedSubCategory}
                </>
              ) : (
                selectedCategory === 'general'
                ? language === 'en' ? 'General/Company' : 'Allgemein'
                : selectedCategory === 'financials'
                ? language === 'en' ? 'Financials' : 'Finanzen'
                : selectedCategory === 'technical'
                ? language === 'en' ? 'Technical/Scientific' : 'Technisch'
                : selectedCategory === 'timing'
                ? language === 'en' ? 'Timing' : 'Zeitplan'
                : selectedCategory === 'applications'
                ? language === 'en' ? 'Other Applications' : 'Apps'
                : selectedCategory === 'other'
                ? language === 'en' ? 'Other' : 'Sonstiges'
                : selectedCategory
              )}
            </span>
          </>
        ) : (
          <>
            {' > '}
            <span className="active-breadcrumb">{language === 'en' ? 'All Input Documents' : 'Alle Eingabedokumente'}</span>
          </>
        )}
      </div>

      {selectedCategory === null ? (
        <div className="category-buttons">
          <button className="category-button" onClick={handleGeneralCompanyClick}>
            <LandPlot className="button-icon" />
            {language === 'en' ? 'General/Company' : 'Allgemein'}
          </button>
          <button className="category-button" onClick={handleFinancialsClick}>
            <ReceiptEuro className="button-icon" />
            {language === 'en' ? 'Financials' : 'Finanzen'}
          </button>
          <button className="category-button" onClick={handleTechnicalClick}>
            <PencilRuler className="button-icon" />
            {language === 'en' ? 'Technical/Scientific' : 'Technisch'}
          </button>
          <button className="category-button" onClick={handleTimingClick}>
            <ChartGantt className="button-icon" />
            {language === 'en' ? 'Timing' : 'Zeitplan'}
          </button>
          <button className="category-button" onClick={handleOtherAppsClick}>
            <ScrollText className="button-icon" />
            {language === 'en' ? 'Other Applications' : 'Apps'}
          </button>
          <button className="category-button" onClick={handleOtherClick}>
            <Ellipsis className="button-icon" />
            {language === 'en' ? 'Other' : 'Sonstiges'}
          </button>
        </div>
      ) : selectedCategory === 'general' ? (
        <div className="category-buttons">
          <button className="category-button" onClick={handleGoBack}>
            <ArrowLeftToLine className="button-icon" />
            {language === 'en' ? 'Go back' : 'Zurück'}
          </button>
        </div>
      ) : selectedCategory === 'financials' ? (
        selectedSubCategory === null ? (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
            <button className="category-button" onClick={handlePayrollClick}>
              <Banknote className="button-icon" />
              {language === 'en' ? 'Payroll' : 'Gehalt'}
            </button>
            <button className="category-button" onClick={handleInvoicesClick}>
              <FileDigit className="button-icon" />
              {language === 'en' ? 'Invoices' : 'Rechnungen'}
            </button>
            <button className="category-button" onClick={handleReportsClick}>
              <FileAxis3d className="button-icon" />
              {language === 'en' ? 'Reports' : 'Berichte'}
            </button>
          </div>
        ) : (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
          </div>
        )
      ) : selectedCategory === 'technical' ? (
        selectedSubCategory === null ? (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
            <button className="category-button" onClick={handleResearchClick}>
              <GraduationCap className="button-icon" />
              {language === 'en' ? 'Research Papers' : 'Forschung'}
            </button>
            <button className="category-button" onClick={handleDocumentationClick}>
              <BrainCircuit className="button-icon" />
              {language === 'en' ? 'Documentation' : 'Dokumentation'}
            </button>
            <button className="category-button" onClick={handleDrawingsClick}>
              <DraftingCompass className="button-icon" />
              {language === 'en' ? 'Drawings' : 'Zeichnungen'}
            </button>
          </div>
        ) : (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
          </div>
        )
      ) : selectedCategory === 'timing' ? (
        <div className="category-buttons">
          <button className="category-button" onClick={handleGoBack}>
            <ArrowLeftToLine className="button-icon" />
            {language === 'en' ? 'Go back' : 'Zurück'}
          </button>
        </div>
      ) : selectedCategory === 'applications' ? (
        <div className="category-buttons">
          <button className="category-button" onClick={handleGoBack}>
            <ArrowLeftToLine className="button-icon" />
            {language === 'en' ? 'Go back' : 'Zurück'}
          </button>
        </div>
      ) : selectedCategory === 'other' ? (
        selectedSubCategory === null ? (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
            <button className="category-button" onClick={handleCompetitionInspirationClick}>
              <Lightbulb className="button-icon" />
              {language === 'en' ? 'Competition & Inspiration' : 'Wettbewerb'}
            </button>
          </div>
        ) : (
          <div className="category-buttons">
            <button className="category-button" onClick={handleGoBack}>
              <ArrowLeftToLine className="button-icon" />
              {language === 'en' ? 'Go back' : 'Zurück'}
            </button>
          </div>
        )
      ) : (
        <div className="category-buttons">
          <button className="category-button" onClick={handleGoBack}>
            <ArrowLeftToLine className="button-icon" />
            {language === 'en' ? 'Go back' : 'Zurück'}
          </button>
        </div>
      )}

      <div className="searching-container">
        <input
          type="text"
          placeholder={language === 'en' ? "Search documents..." : "Dokumente suchen..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {error && <p className="file-list-error">{error}</p>}
      {successMessage && <p className="file-list-success">{successMessage}</p>}

      <table className="file-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('name')}>
              {language === 'en' ? 'Documents' : 'Dokumente'} {sortColumn === 'name' ? (sortOrder === 'asc' ? '⬆' : '⬇') : ''}
            </th>
            <th onClick={() => handleSort('updated_at')}>
              {language === 'en' ? 'Last Modified' : 'Zuletzt geändert'} {sortColumn === 'updated_at' ? (sortOrder === 'asc' ? '⬆' : '⬇') : ''}
            </th>
            <th>{language === 'en' ? 'Modified By' : 'Geändert von'}</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="3">Loading...</td>
            </tr>
          ) : filteredFiles.length > 0 ? (
            filteredFiles.map((file) => (
              <tr key={file.id} className="file-row">
                <td className="small-text">
                  {file.name}
                  {file.is_sensitive ? (
                    <LockOpen
                      alt="Sensitive"
                      size={12} style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                    />
                  ) : null} 
                </td>
                <td className="small-text">
                  {new Date(file.updated_at).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
                <td className="small-text modified-by-cell">
                  <span>{file.fullname || 'Unknown'}</span>
                  <div className="action-buttons">
                    {(file.type === "url/reference" || 
                     file.type?.startsWith("application/") || 
                     file.type?.startsWith("image/")) && (
                      <button
                        className="icon-button"
                        onClick={() => handleViewFile(file)}
                        title="View"
                      >
                        <SquareArrowOutUpRight size={20} />
                      </button>
                    )}
                    {(file.type === "url/reference" || 
                     file.type?.startsWith("text/") ||
                     file.type?.startsWith("application/") || 
                     file.type?.startsWith("image/")) && (
                      <button
                        className="icon-button"
                        onClick={() => handleDisplayFile(file)}
                        title="Display"
                      >
                        <Expand size={20} />
                      </button>
                    )}

                    {(userRole?.startsWith('org') || hasSensitiveAccess) && (
                      <button
                        className="icon-button"
                        onClick={() => handleMarkSensitive(file.id)}
                        title={file.is_sensitive ? "Unmark as sensitive" : "Mark as sensitive"}
                      >
                        {file.is_sensitive ? <LockOpen size={20} /> : <FileLock size={20} />}
                      </button>
                    )}

                    <button
                      className="icon-button"
                      onClick={() => handleRename(file.id)}
                      title="Rename"
                    >
                      <SpellCheck2 size={20} />
                    </button>

                    <button
                      className="icon-button"
                      onClick={() => handleAssignFolderClick(file.id)}
                      title="Assign to Folder"
                    >
                      <FolderInput size={20} />
                    </button>

                    <button
                      className="icon-button"
                      onClick={() => handleDelete(file.id)}
                      title="Delete"
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3">No documents found for this workspace.</td>
            </tr>
          )}
        </tbody>
      </table>

      {assignModalVisible && (
        <div className="modal-overlay">
          <div className="assign-folder-modal">
            <h3>Assign Folder</h3>
            {assignSelectedCategory === null ? (
              <div className="category-buttons">
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('general')}
                >
                  <LandPlot className="button-icon" /> General/Company
                </button>
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('financials')}
                >
                  <ReceiptEuro className="button-icon" /> Financials
                </button>
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('technical')}
                >
                  <PencilRuler className="button-icon" /> Technical/Scientific
                </button>
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('timing')}
                >
                  <ChartGantt className="button-icon" /> Timing
                </button>
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('applications')}
                >
                  <ScrollText className="button-icon" /> Other Applications
                </button>
                <button
                  className="category-button"
                  onClick={() => handleAssignCategoryClick('other')}
                >
                  <Ellipsis className="button-icon" /> Other
                </button>
                <button
                  className="category-button cancel-button"
                  onClick={() => setAssignModalVisible(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="category-buttons">
                <button className="category-button" onClick={handleAssignGoBack}>
                  <ArrowLeftToLine className="button-icon" />
                  Go back
                </button>
                {assignSelectedCategory === 'financials' && (
                  <>
                    <button
                      className="category-button"
                      onClick={() => updateFolderPath(currentAssignFileId, 'input/financials')}
                    >
                      <ReceiptEuro className="button-icon" /> Financials
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('payroll')}
                    >
                      <Banknote className="button-icon" /> Payroll
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('invoices')}
                    >
                      <FileDigit className="button-icon" /> Invoices
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('reports')}
                    >
                      <FileAxis3d className="button-icon" /> Reports
                    </button>
                  </>
                )}
                {assignSelectedCategory === 'technical' && (
                  <>
                    <button
                      className="category-button"
                      onClick={() => updateFolderPath(currentAssignFileId, 'input/technical')}
                    >
                      <PencilRuler className="button-icon" /> Technical/Scientific
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('research')}
                    >
                      <GraduationCap className="button-icon" /> Research Papers
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('documentation')}
                    >
                      <BrainCircuit className="button-icon" /> Documentation
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('drawings')}
                    >
                      <DraftingCompass className="button-icon" /> Drawings
                    </button>
                  </>
                )}
                {assignSelectedCategory === 'other' && (
                  <>
                    <button
                      className="category-button"
                      onClick={() => updateFolderPath(currentAssignFileId, 'input/other')}
                    >
                      <Ellipsis className="button-icon" /> Other
                    </button>
                    <button
                      className="category-button"
                      onClick={() => handleAssignSubCategoryClick('competition')}
                    >
                      <Lightbulb className="button-icon" /> Competition & Inspiration
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {renameModalVisible && (
        <div className="modal-overlay">
          <div className="rename-popup">
            <h3>Rename File</h3>
            <input
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              placeholder="Enter new file name"
              autoFocus
            />
            <div className="rename-popup-buttons">
              <button 
                className="rename-popup-cancel"
                onClick={() => setRenameModalVisible(false)}
              >
                Cancel
              </button>
              <button 
                className="rename-popup-confirm"
                onClick={handleRenameConfirm}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModalVisible && (
        <div className="modal-overlay">
          <div className="rename-popup">
            <h3>Delete File</h3>
            <p>Are you sure you want to delete this file? This action cannot be undone.</p>
            <div className="rename-popup-buttons">
              <button 
                className="rename-popup-cancel"
                onClick={() => setDeleteModalVisible(false)}
              >
                Cancel
              </button>
              <button 
                className="rename-popup-confirm"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Display
        isOpen={displayModalOpen}
        onClose={() => {
          setDisplayModalOpen(false);
          setDisplayUrl(null);
          setDisplayTextContent(null);
          setDisplayType(null);
          setDisplayFileTitle(null);
        }}
        url={displayUrl}
        textContent={displayTextContent}
        type={displayType}
        title={displayFileTitle}
      />
    </div>
  );
}

export default FileList;
